/**
 * R22 addendum — the §29.2 `user-text` client-message wire shape.
 *
 * EVERY typed submission from the Type composer is sent to the server as a `user-text` RTVI client
 * message; the SERVER decides armed-capture (an out-of-flow email/phone the composer was pulsed for,
 * §29 / K2) vs normal chat — the composer sends the raw typed text verbatim either way. This replaces
 * the transport's `sendText` route, which the server-side armed-capture channel could not observe (so
 * the armed capture was inert).
 *
 * `sendClientMessage(type, data)` serialises to `{ type, data }`, so the wire message is:
 *   { "type": "user-text", "data": { "text": <typed text> } }
 * matching the frozen captured fixtures `r42-user-text-armed-email.json` / `r42-user-text-chat.json`.
 *
 * This pure helper builds the `data` payload so the exact wire shape is unit-testable against those
 * fixtures without a React/RTVI harness (the kiosk suite has no jsdom). `useVoice.sendUserText` calls
 * `client.sendClientMessage(USER_TEXT_MSG_TYPE, buildUserTextData(text))`.
 */

/** The RTVI client-message type for a typed turn (§29.2). */
export const USER_TEXT_MSG_TYPE = 'user-text' as const;

/** Build the `data` payload for a `user-text` message: `{ text }` (trimmed by the caller). */
export function buildUserTextData(text: string): { text: string } {
  return { text };
}

/**
 * Build the FULL wire message a `sendClientMessage('user-text', data)` produces — used by the tests to
 * assert the composer emits the frozen §29.2 shape verbatim. Kept here (not just in the test) so the
 * canonical shape lives in one place next to the type + data builder.
 */
export function buildUserTextMessage(text: string): {
  type: typeof USER_TEXT_MSG_TYPE;
  data: { text: string };
} {
  return { type: USER_TEXT_MSG_TYPE, data: buildUserTextData(text) };
}
